import { Injectable } from '@angular/core';

/**
 * Detects whether the Angular app is running inside the Visarc Timer app
 * (the Electron shell) vs. a regular browser tab.
 *
 * The Electron preload (VisarcDesktopApp/src/preload.cts) injects
 * `window.visarcDesktop`; in a normal browser this is undefined.
 *
 * Used by the default layout to hide nav/timer-header/info-button when running
 * inside the timer app, and by the login flow to redirect to /desktop after
 * sign-in instead of /home.
 */
@Injectable({ providedIn: 'root' })
export class DesktopAppService {
    constructor() {
        // Tag the document so global CSS rules (in styles.scss) can hide
        // nav-menu / timer-header / app-info-button when running inside the
        // timer app. The same class is also added by an inline script in
        // index.html — this is a belt-and-braces fallback in case that script
        // runs before window.visarcDesktop is set.
        if (this.isTimerApp && typeof document !== 'undefined') {
            document.documentElement.classList.add('visarc-timer-app');
        }
    }

    /** True when running inside the Visarc Timer app (Electron shell).
     *
     * Implemented as a getter (not a one-shot field initializer) so the value
     * reflects `window.visarcDesktop` at every read. That way, if anything
     * constructs this service before the preload has finished injecting the
     * API onto `window` (rare, but possible during bootstrap edge cases),
     * subsequent reads still pick up the correct value. */
    get isTimerApp(): boolean {
        return typeof window !== 'undefined' && !!window.visarcDesktop;
    }

    /** True when the host Electron build uses `titleBarStyle: 'hidden'` +
     *  `titleBarOverlay` — i.e. it reserves 32px at the top of the window
     *  and expects the renderer to paint a custom title bar inside that
     *  strip. Chromium only exposes `navigator.windowControlsOverlay`
     *  when that config is active, so an OLD installer (where the OS
     *  drew its own title bar) returns false here, an installer at the
     *  current config returns true.
     *
     *  Used by `<desktop-shell>` to decide whether to render
     *  `<app-title-bar>` and reserve the 32px padding. Old-installer
     *  users loading the new Angular site won't get the custom strip
     *  drawn on top of their OS-drawn title bar; new-installer users
     *  loading old Angular won't see a 32px empty band overlapping the
     *  OS buttons. The renderer adapts to whichever shell is hosting it. */
    get hasWindowControlsOverlay(): boolean {
        if (typeof navigator === 'undefined') return false;
        const wco = (navigator as { windowControlsOverlay?: { visible?: boolean } }).windowControlsOverlay;
        return !!wco;
    }
}
